import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import T from "../styles/tokens";
import { PFLogo, Btn, Ic, Spinner } from "../components/UI";
import * as api from "../utils/api";

export default function ClientLanding() {
  const { token } = useParams();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.getInvitationDetails(token)
      .then(setDetails)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: T.gray100, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spinner size={40} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: T.gray100, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ background: T.white, borderRadius: 16, padding: 40, maxWidth: 480, textAlign: "center", boxShadow: T.shadowLg }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: T.redBg, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", color: T.red, fontSize: 24 }}>✕</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: T.indigo, fontFamily: T.font, margin: "0 0 10px" }}>Link Unavailable</h2>
          <p style={{ fontSize: 14, color: T.charcoal, fontFamily: T.font, lineHeight: 1.6 }}>{error}</p>
          <p style={{ fontSize: 13, color: T.gray400, fontFamily: T.font, marginTop: 20 }}>
            Need help? Contact us at <a href="mailto:support@pathfindtech.com" style={{ color: T.purple, fontWeight: 600 }}>support@pathfindtech.com</a>
          </p>
        </div>
      </div>
    );
  }

  const consentUrl = api.getConsentUrl(token);

  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(160deg, ${T.indigo} 0%, ${T.purple} 50%, ${T.indigo} 100%)` }}>
      <div className="pf-public-shell" style={{ maxWidth: 640, margin: "0 auto", padding: "60px 24px" }}>
        {/* Logo Header */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
            <PFLogo size={40} />
            <div style={{ textAlign: "left" }}>
              <div style={{ color: T.white, fontSize: 18, fontWeight: 700, fontFamily: T.font }}>Pathfinder Technical Services</div>
              <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 11, fontWeight: 500, fontFamily: T.font }}>Managed IT Services</div>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div style={{ background: T.white, borderRadius: 16, boxShadow: T.shadowLg, overflow: "hidden" }}>
          {/* Header */}
          <div className="pf-public-card-pad" style={{ background: `linear-gradient(135deg, ${T.gradStart}, ${T.gradEnd})`, padding: "28px 32px", color: T.white }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Ic.Shield />
              <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: T.font }}>Secure Onboarding</span>
            </div>
            <h1 className="pf-public-h1" style={{ fontSize: 22, fontWeight: 700, fontFamily: T.font, margin: 0 }}>
              {details.clientName}, let's get connected
            </h1>
            <p style={{ fontSize: 14, opacity: 0.8, fontFamily: T.font, marginTop: 6 }}>
              {details.templateName} access template
            </p>
          </div>

          <div className="pf-public-card-pad" style={{ padding: "28px 32px" }}>
            {/* What this does */}
            <h3 style={{ fontSize: 14, fontWeight: 700, color: T.indigo, fontFamily: T.font, marginBottom: 14 }}>What this will do</h3>
            <div style={{ marginBottom: 24 }}>
              {details.plainEnglish.map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: i < details.plainEnglish.length - 1 ? `1px solid ${T.gray100}` : "none" }}>
                  <span style={{ color: T.green, marginTop: 2, flexShrink: 0 }}>✓</span>
                  <span style={{ fontSize: 14, color: T.charcoal, fontFamily: T.font, lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>

            {/* Roles */}
            {details.roles.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: T.indigo, fontFamily: T.font, marginBottom: 10 }}>Admin roles requested</h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {details.roles.map((r) => (
                    <span key={r} style={{ padding: "4px 10px", borderRadius: 4, background: "#F0EAFF", color: T.purple, fontSize: 12, fontWeight: 500, fontFamily: T.font }}>{r}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Expiry */}
            <div style={{ background: T.amberBg, borderRadius: T.radiusSm, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
              <Ic.Clock />
              <span style={{ fontSize: 12, color: T.amber, fontWeight: 600, fontFamily: T.font }}>
                This link expires {new Date(details.expiresAt).toLocaleString()}
              </span>
            </div>

            {/* Consent Button */}
            <a
              href={consentUrl}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                width: "100%", padding: "14px 24px",
                borderRadius: T.radiusSm,
                background: T.purple, color: T.white,
                fontSize: 15, fontWeight: 700, fontFamily: T.font,
                textDecoration: "none",
                boxShadow: "0 4px 16px rgba(70,10,186,0.3)",
                transition: "all 0.15s",
              }}
            >
              <Ic.MS /> Approve & Connect with Microsoft
            </a>

            <p style={{ fontSize: 11.5, color: T.gray400, fontFamily: T.font, textAlign: "center", marginTop: 14, lineHeight: 1.5 }}>
              Clicking this button will redirect you to Microsoft's sign-in page. You must sign in with a Global Administrator account for your organization.
            </p>
          </div>
        </div>

        {/* Footer */}
        <p style={{ textAlign: "center", fontSize: 11.5, color: "rgba(255,255,255,0.35)", fontFamily: T.font, marginTop: 24 }}>
          Pathfinder Technical Services • pathfindtech.com
        </p>
      </div>
    </div>
  );
}
